import { useState } from "react";
import {
  Box, Typography, Paper, Grid,
  Button, Table, TableHead, TableRow,
  TableCell, TableBody, CircularProgress,
  Alert, Dialog, TextField, Collapse,
  IconButton, Chip, Divider, TableContainer
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";

import api from "../service/api";
import { entryReportService } from "../service/EntryReportService";

/* ── helpers ── */
const fmt = (v) =>
  v ? new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—";

const fmtPrice = (v) =>
  v != null ? `₹${Number(v).toLocaleString("en-IN")}` : "—";

const VEHICLE_LABEL = { E_RICKSHAW: "E-Rickshaw", SCOOTY: "Scooty" };
const VEHICLE_COLOR = { E_RICKSHAW: "success", SCOOTY: "secondary" };

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

/* ── group vehicles by location ── */
const groupByLocation = (vehicles = []) => {
  const grouped = {};
  vehicles.forEach((v) => {
    const loc = v.location || "Unknown";
    if (!grouped[loc]) grouped[loc] = [];
    grouped[loc].push(v);
  });
  return grouped;
};

/* ══════════════════════════════════════════════════════════════ */
const EntryReport = () => {

  const [mode,          setMode]          = useState("monthly");
  const [year,          setYear]          = useState(new Date().getFullYear());
  const [date,          setDate]          = useState("");
  const [selectedMonth, setSelectedMonth] = useState(null);

  /* grouped data: { NEW: {loc: [vehicles]}, EXCHANGE: {loc: [vehicles]} } */
  const [tableData, setTableData] = useState({ NEW: {}, EXCHANGE: {} });
  const [summary,   setSummary]   = useState(null);

  /* weekly state — always an object with these 3 keys */
  const [weekList,     setWeekList]     = useState({ allWeekNos: [], newWeeks: [], exchangeWeeks: [] });
  const [selectedWeek, setSelectedWeek] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState("");

  /* view dialog */
  const [openView, setOpenView] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [viewLoad, setViewLoad] = useState(false);

  /* collapse per source+location key */
  const [collapsed, setCollapsed] = useState({});
  const toggleSection = (key) => setCollapsed((p) => ({ ...p, [key]: !p[key] }));

  /* ── parse API response into tableData ── */
  const parseResponse = (res) => {
    const newVehicles      = res?.data?.data?.new?.vehicles      || res?.data?.new?.vehicles      || [];
    const exchangeVehicles = res?.data?.data?.exchange?.vehicles || res?.data?.exchange?.vehicles || [];
    setTableData({
      NEW:      groupByLocation(newVehicles),
      EXCHANGE: groupByLocation(exchangeVehicles),
    });
    setSummary(res?.data?.summary || res?.data?.data?.summary || null);
    setCollapsed({});
  };

  /* ── MONTHLY ── */
  const handleMonthClick = async (month) => {
    try {
      setLoading(true); setError("");
      setSelectedMonth(month); setWeekList([]); setSelectedWeek(null);
      const res = await entryReportService.getMonthly(year, month);
      parseResponse(res);
    } catch { setError("Failed to load monthly data"); }
    finally  { setLoading(false); }
  };

  /* ── DAILY ── */
  const handleDaily = async () => {
    if (!date) return;
    try {
      setLoading(true); setError("");
      setSelectedMonth(null); setWeekList([]); setSelectedWeek(null);
      const res = await entryReportService.getByDate(date);
      parseResponse(res);
    } catch { setError("Failed to load daily data"); }
    finally  { setLoading(false); }
  };

  /* ── WEEKLY load ── */
  const handleWeekly = async () => {
    try {
      setLoading(true); setError("");
      setSelectedMonth(null); setSelectedWeek(null);
      setTableData({ NEW: {}, EXCHANGE: {} });
      setSummary(null);

      const res = await entryReportService.getWeekly(year);

      // DEBUG — remove after confirming it works
      console.log("Weekly raw response:", JSON.stringify(res?.data, null, 2));

      // Backend returns: { success, year, summary, data: { new: [], exchange: [] } }
      // axios wraps it: res.data = { success, year, summary, data: {...} }
      // So the arrays are at: res.data.data.new  and  res.data.data.exchange
      const payload = res?.data;

      // Handle both possible shapes just in case
      // Shape A (correct): payload.data.new
      // Shape B (flat):    payload.new
      const newWeeks = (
        payload?.data?.new ??
        payload?.new ??
        []
      );
      const exchangeWeeks = (
        payload?.data?.exchange ??
        payload?.exchange ??
        []
      );

      console.log("newWeeks:", newWeeks);
      console.log("exchangeWeeks:", exchangeWeeks);

      if (!Array.isArray(newWeeks) || !Array.isArray(exchangeWeeks)) {
        setError("Unexpected response format from server. Check console.");
        return;
      }

      if (newWeeks.length === 0 && exchangeWeeks.length === 0) {
        setError(`No entries found for year ${year}`);
        setWeekList({ allWeekNos: [], newWeeks: [], exchangeWeeks: [] });
        setSummary(null);
        return;
      }

      // Merged sorted week number list
      const allWeekNos = [
        ...new Set([
          ...newWeeks.map((w) => w.week),
          ...exchangeWeeks.map((w) => w.week),
        ])
      ].sort((a, b) => a - b);

      setWeekList({ allWeekNos, newWeeks, exchangeWeeks });
      setSummary(payload?.summary || null);

    } catch (err) {
      console.error("Weekly load error:", err);
      setError(`Failed to load weekly data: ${err?.response?.data?.message || err.message}`);
    } finally {
      setLoading(false);
    }
  };

  /* ── WEEK click ── */
  const handleWeekClick = (weekNo) => {
    setSelectedWeek(weekNo);

    const nw = weekList.newWeeks.find((w) => w.week === weekNo);
    const ex = weekList.exchangeWeeks.find((w) => w.week === weekNo);

    setTableData({
      NEW:      groupByLocation(nw?.vehicles || []),
      EXCHANGE: groupByLocation(ex?.vehicles || []),
    });

    // Per-week summary
    setSummary({
      totalNew:      nw?.totalEntry      || 0,
      totalExchange: ex?.totalEntry      || 0,
      grandTotal:   (nw?.totalEntry || 0) + (ex?.totalEntry || 0),
      totalNewInvestment:      nw?.totalInvestment || 0,
      totalExchangeInvestment: ex?.totalInvestment || 0,
    });

    setCollapsed({});
  };

  /* ── VIEW ── */
  const handleView = async (id) => {
    try {
      setViewLoad(true); setOpenView(true); setViewData(null);
      const res = await api.get(`/specific-bike-details/${id}`);
      setViewData(res?.data?.data || null);
      console.log(res.data.data)
    } catch { setError("Failed to load details"); }
    finally  { setViewLoad(false); }
  };

  /* ── section keys for collapse ── */
  const allSectionKeys = [
    ...Object.keys(tableData.NEW).map((loc)      => `NEW-${loc}`),
    ...Object.keys(tableData.EXCHANGE).map((loc) => `EXCHANGE-${loc}`),
  ];
  const hasData = allSectionKeys.length > 0;

  /* ── render one location table block ── */
  const renderLocationBlock = (source, loc, vehicles) => {
    const key    = `${source}-${loc}`;
    const isOpen = !collapsed[key];
    const color  = source === "NEW" ? "#1565c0" : "#e65100";
    const bgHead = source === "NEW" ? "#e3f2fd"  : "#fff3e0";

    return (
    <Box sx={{ maxWidth: "1200px", mx: "auto", pl: 4  }}>
        {/* Header */}
        <Box onClick={() => toggleSection(key)} sx={{
          pl: 6,pr:2, py: 1.2, cursor: "pointer", userSelect: "none",
          bgcolor: bgHead,
          display: "flex", alignItems: "center", justifyContent: "space-between",
          "&:hover": { filter: "brightness(0.96)" }, transition: "filter 0.15s"
        }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Chip label={source} size="small"
              sx={{ bgcolor: color, color: "#fff", fontWeight: 700, fontSize: 10 }} />
            <Typography fontWeight={700} fontSize={14}>📍 {loc}</Typography>
            <Chip label={`${vehicles.length} vehicle(s)`} size="small" variant="outlined"
              sx={{ borderColor: color, color, fontWeight: 600, fontSize: 10 }} />
          </Box>
          <IconButton size="small">
            {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Box>

        {/* Table */}
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#fafafa" }}>
                  {["#","Sl No","Type","Company","Model","Colour","Engine No",
                    "Serial No","Battery","OAM","Controller No",
                    "Sale ₹","Transport ₹","Status","Invoice No","Date","Action"
                  ].map((h) => (
                    <TableCell key={h} sx={{ fontWeight: 700, whiteSpace: "nowrap", fontSize: 11 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {vehicles.map((v, idx) => (
                  <TableRow key={v._id} sx={{ "&:hover": { bgcolor: "#f5f8ff" } }}>
                    <TableCell sx={{ fontSize: 11, color: "text.secondary" }}>{idx + 1}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{v.slNo ?? "—"}</TableCell>
                    <TableCell>
                      <Chip label={VEHICLE_LABEL[v.vehicleType] || v.vehicleType || "—"}
                        color={VEHICLE_COLOR[v.vehicleType] || "default"} size="small"
                        sx={{ fontSize: 10 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{v.companyName || "—"}</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>{v.model || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{v.colour || "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{v.engine || "—"}</TableCell>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>{v.chasis || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{v.batteryType || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{v.oamBattery ? "✅" : "—"}</TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{v.controllerNo || "—"}</TableCell>
                  
                    <TableCell sx={{ fontSize: 12 }}>{fmtPrice(v.salePrice)}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{fmtPrice(v.transportPrice)}</TableCell>
                    <TableCell>
                      <Chip label={v.status || "—"}
                        color={v.status === "IN_STOCK" ? "success" : "error"} size="small"
                        sx={{ fontSize: 10 }} />
                    </TableCell>
                    <TableCell sx={{ fontSize: 11 }}>{v.invoiceNo || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 11, whiteSpace: "nowrap" }}>{fmt(v.date)}</TableCell>
                    <TableCell>
                      <Button variant="contained" size="small"
                        onClick={() => handleView(v._id)}
                        sx={{ textTransform: "none", fontSize: 11, py: 0.25, whiteSpace: "nowrap" }}>
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Collapse>
      </Box>
    );
  };

  /* ══════════════════════════════════════════════════════════════
     RENDER
  ══════════════════════════════════════════════════════════════ */
  return (
    <Box p={{ xs: 1, md: 3 }}>

      <Typography variant="h5" fontWeight={700} mb={2}>EV Entry Report</Typography>

      {/* ── MODE SWITCH ── */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {["daily","weekly","monthly"].map((m) => (
            <Grid item key={m}>
              <Button
                variant={mode === m ? "contained" : "outlined"}
                onClick={() => { setMode(m); setTableData({ NEW: {}, EXCHANGE: {} }); setSummary(null); setWeekList({ allWeekNos: [], newWeeks: [], exchangeWeeks: [] }); setSelectedWeek(null); setSelectedMonth(null); }}
                sx={{ textTransform: "capitalize" }}
              >
                {m}
              </Button>
            </Grid>
          ))}
          <Grid item xs={6} sm={3} md={2}>
            <TextField size="small" label="Year" type="number" fullWidth
              value={year} onChange={(e) => setYear(e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      {/* ── DAILY UI ── */}
      {mode === "daily" && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={5} md={3}>
              <TextField type="date" fullWidth size="small" label="Select Date"
                InputLabelProps={{ shrink: true }}
                value={date} onChange={(e) => setDate(e.target.value)} />
            </Grid>
            <Grid item>
              <Button variant="contained" onClick={handleDaily} disabled={!date}>Load</Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* ── WEEKLY UI ── */}
      {mode === "weekly" && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Button variant="contained" onClick={handleWeekly}>Load Weekly Data</Button>

          {weekList.allWeekNos.length > 0 && (
            <Box mt={1.5}>
              <Typography variant="caption" color="text.secondary" mb={1} display="block">
                Click a week to view its vehicles. Blue = has NEW stock, Orange = has EXCHANGE stock.
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1}>
                {weekList.allWeekNos.map((wNo) => {
                  const nw = weekList.newWeeks.find((w) => w.week === wNo);
                  const ex = weekList.exchangeWeeks.find((w) => w.week === wNo);
                  const total = (nw?.totalEntry || 0) + (ex?.totalEntry || 0);
                  return (
                    <Button key={wNo} size="small"
                      variant={selectedWeek === wNo ? "contained" : "outlined"}
                      onClick={() => handleWeekClick(wNo)}
                      sx={{ minWidth: 80, flexDirection: "column", py: 0.5 }}>
                      <span style={{ fontWeight: 700 }}>Wk {wNo}</span>
                      <span style={{ fontSize: 10, opacity: 0.85 }}>{total} vehicle{total !== 1 ? "s" : ""}</span>
                    </Button>
                  );
                })}
              </Box>
            </Box>
          )}
        </Paper>
      )}

      {/* ── MONTHLY UI ── */}
      {mode === "monthly" && (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={1}>
            {MONTHS.map((name, i) => (
              <Grid item xs={4} sm={3} md={2} key={i}>
                <Button fullWidth size="small"
                  variant={selectedMonth === i + 1 ? "contained" : "outlined"}
                  onClick={() => handleMonthClick(i + 1)}
                  sx={{ textTransform: "none" }}>
                  {name}
                </Button>
              </Grid>
            ))}
          </Grid>
        </Paper>
      )}

      {loading && <Box display="flex" justifyContent="center" py={3}><CircularProgress /></Box>}
      {error   && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── SUMMARY CHIPS ── */}
      {summary && !loading && (
        <Box display="flex" gap={1} flexWrap="wrap" mb={2}>
          <Chip label={`Grand Total: ${summary.grandTotal ?? 0}`} color="primary" />
          <Chip label={`NEW: ${summary.totalNew ?? 0}`}           color="info"    variant="outlined" />
          <Chip label={`EXCHANGE: ${summary.totalExchange ?? 0}`} color="warning" variant="outlined" />
          {summary.totalNewInvestment > 0 && (
            <Chip label={`NEW Investment: ${fmtPrice(summary.totalNewInvestment)}`} color="info" size="small" variant="outlined" />
          )}
          {summary.totalExchangeInvestment > 0 && (
            <Chip label={`Exchange Investment: ${fmtPrice(summary.totalExchangeInvestment)}`} color="warning" size="small" variant="outlined" />
          )}
          {summary.byVehicleType && Object.entries(summary.byVehicleType).map(([type, val]) => (
            <Chip key={type}
              label={`${VEHICLE_LABEL[type] || type}: ${(val.new ?? 0) + (val.exchange ?? 0)}`}
              color={VEHICLE_COLOR[type] || "default"} variant="outlined" size="small" />
          ))}
        </Box>
      )}

      {/* ── EXPAND / COLLAPSE ALL ── */}
      {hasData && !loading && (
        <Box display="flex" gap={1} mb={1.5}>
          <Button size="small" variant="outlined"
            onClick={() => setCollapsed(Object.fromEntries(allSectionKeys.map((k) => [k, false])))}>
            Expand All
          </Button>
          <Button size="small" variant="outlined"
            onClick={() => setCollapsed(Object.fromEntries(allSectionKeys.map((k) => [k, true])))}>
            Collapse All
          </Button>
        </Box>
      )}

      {/* ── NEW VEHICLES ── */}
      {!loading && Object.keys(tableData.NEW).length > 0 && (
        <Box mb={1}>
          <Typography variant="subtitle1" fontWeight={700} color="#1565c0" mb={1}>
            🆕 NEW Vehicles
          </Typography>
          {Object.entries(tableData.NEW).map(([loc, vehicles]) =>
            renderLocationBlock("NEW", loc, vehicles)
          )}
        </Box>
      )}

      {/* ── EXCHANGE VEHICLES ── */}
      {!loading && Object.keys(tableData.EXCHANGE).length > 0 && (
        <Box>
          <Typography variant="subtitle1" fontWeight={700} color="#e65100" mb={1}>
            🔁 EXCHANGE Vehicles
          </Typography>
          {Object.entries(tableData.EXCHANGE).map(([loc, vehicles]) =>
            renderLocationBlock("EXCHANGE", loc, vehicles)
          )}
        </Box>
      )}

      {!loading && !hasData && (summary !== null) && (
        <Alert severity="info">No vehicles found for the selected period.</Alert>
      )}

      {/* ══════════════════════════════════════════════════════
          VIEW DIALOG — flat response (no stockDetails nesting)
      ══════════════════════════════════════════════════════ */}
      <Dialog open={openView} onClose={() => { setOpenView(false); setViewData(null); }}
        fullWidth maxWidth="md" PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box p={3} sx={{ background: "#f8fafc" }}>
          <Typography variant="h6" fontWeight={700} mb={0.5}>EV Vehicle — Full Details</Typography>
          <Divider sx={{ mb: 2 }} />

          {viewLoad && (
            <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>
          )}
          {!viewLoad && !viewData && <Alert severity="info">No data found</Alert>}

          {!viewLoad && viewData && (
            <Grid container spacing={2}>

              {/* Vehicle Info */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1.5}>🛵 Vehicle Info</Typography>
                  {[
                    ["Sl No",          viewData?.slNo],
                    ["Vehicle Type",   VEHICLE_LABEL[viewData?.vehicleType] || viewData?.vehicleType],
                    ["Company",        viewData?.companyName],
                    ["Model",          viewData?.model],
                    ["Colour",         viewData?.colour],
                    ["Engine No",      viewData?.engine],
                    ["Serial No",      viewData?.chasis],
                    ["Invoice No",     viewData?.invoiceNo],
                    ["HSN Code",       viewData?.HSN],
                    ["Location",       viewData?.currentLocation || viewData?.location],
                    ["Status",         viewData?.status],
                    ["Source",         viewData?.source],
                    ["Entry Date",     fmt(viewData?.date || viewData?.createdAt)],
                    ["Sale Date",      fmt(viewData?.saleDate)],
                  ].map(([label, val]) => (
                    <Box key={label} display="flex" justifyContent="space-between"
                      py={0.4} borderBottom="1px solid #f1f5f9">
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={600}>{val ?? "—"}</Typography>
                    </Box>
                  ))}
                
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Sale Price</Typography>
                    <Typography variant="body2" fontWeight={700}>{fmtPrice(viewData?.salePrice)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Transport Price</Typography>
                    <Typography variant="body2" fontWeight={600}>{fmtPrice(viewData?.transportPrice)}</Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">Total Cost</Typography>
                    <Typography variant="body2" fontWeight={700} color="primary.main">
                      {fmtPrice(viewData?.totalCost)}
                    </Typography>
                  </Box>
                </Paper>
              </Grid>

              {/* Battery & Warranty */}
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1.5}>🔋 Battery & Warranty</Typography>
                  {[
                    ["Battery Type",          viewData?.batteryType],
                    ["OAM Battery",           viewData?.oamBattery ? "✅ Yes" : "❌ No"],
                    ["Warranty Period",        fmt(viewData?.warrantyPeriod)],
                    ["Controller No",          viewData?.controllerNo],
                    ["Controller Warranty",    fmt(viewData?.controllerWarranty)],
                    ["Motor Warranty",         fmt(viewData?.motorWarranty)],
                    ["Charger",                viewData?.charger],
                    ["Charger Warranty",       fmt(viewData?.chargerWarranty)],
                    ["Form-22 Received",       viewData?.form22Received ? "✅ Yes" : "❌ No"],
                    ["Invoice Status",         viewData?.invoiceStatus ? "✅ Done" : "❌ Pending"],
                    ["Quantity",               viewData?.quantity],
                    ["Remarks",                viewData?.remarks],
                  ].map(([label, val]) => (
                    <Box key={label} display="flex" justifyContent="space-between"
                      py={0.4} borderBottom="1px solid #f1f5f9">
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={600}>{val ?? "—"}</Typography>
                    </Box>
                  ))}
                </Paper>

                <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2, mt: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1}>👤 Created By</Typography>
                  {[
                    ["Name",  viewData?.createdBy?.name],
                    ["Email", viewData?.createdBy?.email],
                  ].map(([label, val]) => (
                    <Box key={label} display="flex" justifyContent="space-between"
                      py={0.4} borderBottom="1px solid #f1f5f9">
                      <Typography variant="body2" color="text.secondary">{label}</Typography>
                      <Typography variant="body2" fontWeight={600}>{val ?? "—"}</Typography>
                    </Box>
                  ))}
                </Paper>
              </Grid>

              {/* Location Cost */}
              {viewData?.locationCost?.length > 0 && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
                    <Typography variant="subtitle1" fontWeight={700} mb={1.5}>📍 Location Cost Tracking</Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: "#f0faf8" }}>
                          <TableCell><b>Location</b></TableCell>
                          <TableCell><b>Total Cost</b></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {viewData.locationCost.map((lc, i) => (
                          <TableRow key={i}>
                            <TableCell>{lc.location}</TableCell>
                            <TableCell>{fmtPrice(lc.totalCost)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Paper>
                </Grid>
              )}

              {/* Transfer History */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1.5}>🔁 Transfer History</Typography>
                  {viewData?.transferHistory?.length > 0 ? (
                    viewData.transferHistory.map((t, i) => (
                      <Box key={i} sx={{ p: 1.5, mb: 1, borderRadius: 1.5, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
                        <Typography variant="body2" fontWeight={700}>
                          {t.fromLocation || "—"} ➝ {t.toLocation || "—"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          Challan: {t.transferChallanNo || "—"} &nbsp;|&nbsp; Cost: {fmtPrice(t.transferCost)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          By: {t.transferredBy?.name || t.transferredBy || "—"}
                          &nbsp;|&nbsp; Date: {fmt(t.date)}
                        </Typography>
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" color="text.secondary">No transfer history</Typography>
                  )}
                </Paper>
              </Grid>

              {/* Sale Info */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2.5, borderRadius: 2, boxShadow: 2 }}>
                  <Typography variant="subtitle1" fontWeight={700} mb={1.5}>💰 Sale Info</Typography>
                  {viewData?.saleDetails ? (
                    <Grid container spacing={1}>
                      {[
                        ["Customer",     viewData.saleDetails.customerName],
                        ["Mobile",       (viewData.saleDetails.mobile || []).join(", ")],
                        ["Sale Price",   fmtPrice(viewData.saleDetails.salePrice)],
                        ["Sale Type",    viewData.saleDetails.saleType],
                        ["Invoice No",   viewData.saleDetails.invoiceNo],
                        ["Sale Date",    fmt(viewData.saleDetails.saleDate)],
                        ["Engine No",    viewData.saleDetails.engineNumber],
                        ["Serial No",    viewData.saleDetails.chasisNumber],
                      ].map(([label, val]) => (
                        <Grid item xs={12} sm={6} key={label}>
                          <Typography variant="body2" color="text.secondary">{label}</Typography>
                          <Typography variant="body2" fontWeight={600}>{val || "—"}</Typography>
                        </Grid>
                      ))}
                    </Grid>
                  ) : (
                    <Alert severity="info" sx={{ py: 0.5 }}>Vehicle not sold yet</Alert>
                  )}
                </Paper>
              </Grid>

            </Grid>
          )}

          <Box mt={2} display="flex" justifyContent="flex-end">
            <Button variant="contained" onClick={() => { setOpenView(false); setViewData(null); }}
              sx={{ textTransform: "none" }}>
              Close
            </Button>
          </Box>
        </Box>
      </Dialog>

    </Box>
  );
};

export default EntryReport;